import { DatePicker, Form, type FormProps, Input, InputNumber, Modal, Select, Switch } from "antd";
import dayjs from "dayjs";
import { isEmpty } from "lodash-es";
import { useCallback, useEffect, useState } from "react";
import { useIntl } from "react-intl";
import styled from "styled-components";
import SelectCompetition from "~components/SelectCompetition/SelectCompetition.tsx";
import SelectFixture from "~components/SelectFixture/SelectFixture.tsx";
import SelectPerson from "~components/SelectPerson/SelectPerson.tsx";
import SelectRole from "~components/SelectRole/SelectRole.tsx";
import SelectSeason from "~components/SelectSeason/SelectSeason.tsx";
import { formItemSharedStyles } from "~constants/sharedStyles.ts";
import { ConductFineStatuses } from "~enums/conductFineStatuses.ts";
import { ConductStatuses } from "~enums/conductStatuses.ts";
import { useGetBuildFixtureNameFunc } from "~hooks/useGetBuildFixtureNameFunc.ts";
import { useIsPersonInRoster } from "~pages/Conducts/ConductUpsertModal/hooks/useIsPersonInRoster.ts";
import type { LabelValue } from "~types/labelValue.ts";
import { sortStringValues } from "~utils/utilities.ts";
import { useGetConductTypeOptions } from "../ConductListPage/hooks/useGetConductTypeOptions.ts";
import type { ConductTransformed } from "../types/conductTransformed.ts";
import type { UpsertConductData } from "./types/upsertConductData.ts";
import type { UpsertConductForm } from "./types/upsertConductForm.ts";

const StyledForm = styled(Form)<FormProps<UpsertConductForm>>`
    .ant-form-item-label {
        width: 100%; /* Sets the label width to take full available space */
    }
`;

const fineStatuses = Object.values(ConductFineStatuses)
  .sort(sortStringValues)
  .map((x) => ({ label: x, value: x }));
const statuses = Object.values(ConductStatuses)
  .sort(sortStringValues)
  .map((x) => ({ label: x, value: x }));

const RolePlayer = "PLAYER";

type Props = {
  conduct?: ConductTransformed;
  onUpsert: (upsertData: UpsertConductData) => void;
  onClose: () => void;
  isUpsertInProcess: boolean;
};

const ConductUpsertModal = ({ conduct, onUpsert, onClose, isUpsertInProcess }: Props) => {
  const { formatMessage } = useIntl();
  const conductTypes = useGetConductTypeOptions();
  const [form] = Form.useForm<UpsertConductForm>();
  const competition: LabelValue | null = Form.useWatch("competition", form);
  const season: LabelValue | null = Form.useWatch("season", form);
  const fixture: LabelValue | null = Form.useWatch("fixture", form);
  const person: LabelValue | null = Form.useWatch("person", form);
  const role: LabelValue | null = Form.useWatch("role", form);
  const isEdit = !isEmpty(conduct);
  const { mutateAsync: isPersonInRoster } = useIsPersonInRoster();
  const [showPersonNotPresentInRosterWarning, setShowPersonNotPresentInRosterWarning] = useState<boolean>(false);

  const showPersonNotPresentInRosterWarningMessage = formatMessage({
    id: fixture ? "conduct.upsert.person.not.present.in.fixture" : "conduct.upsert.person.not.present.in.season",
  });

  const buildFixtureName = useGetBuildFixtureNameFunc();

  const formInitValues: UpsertConductForm = {
    competition: conduct?.competition
      ? {
          value: conduct.competition.competitionId,
          label: conduct.competition.nameLocal,
        }
      : null,
    season: conduct?.season ? { value: conduct.season.seasonId, label: conduct.season.nameLocal } : null,
    fixture: conduct?.fixture
      ? {
          value: conduct.fixture.fixtureId,
          label: buildFixtureName(conduct.fixture, conduct.resources),
        }
      : null,
    person: conduct?.person ? { value: conduct.person.personId, label: conduct.person.nameFullLocal } : null,
    role:
      conduct !== undefined && conduct !== null
        ? conduct.role
          ? { label: conduct.role, value: conduct.role }
          : {
              label: formatMessage({ id: "conduct.role.player" }),
              value: RolePlayer,
            }
        : null,
    conductType: conduct?.conductType || [],
    conductNotes: conduct?.conductNotes || "",
    dateFinePaidLocal: conduct?.dateFinePaidLocal ? dayjs(conduct.dateFinePaidLocal) : null,
    fineAmount: conduct?.fineAmount || null,
    fineCurrency: conduct?.fineCurrency || null,
    dateOffenceLocal: conduct?.dateOffenceLocal ? dayjs(conduct.dateOffenceLocal) : null,
    dateSuspendedFrom: conduct?.dateSuspendedFrom ? dayjs(conduct.dateSuspendedFrom) : null,
    dateSuspendedTo: conduct?.dateSuspendedTo ? dayjs(conduct.dateSuspendedTo) : null,
    status: conduct?.status || ConductStatuses.Pending,
    fineStatus: conduct?.fineStatus || null,
    lifeSentence: conduct?.lifeSentence || false,
  };

  useEffect(() => {
    if (role?.value === RolePlayer && person && (season || fixture)) {
      isPersonInRoster({
        seasonId: season?.value,
        fixtureId: fixture?.value,
        personId: person.value,
      }).then((result) => setShowPersonNotPresentInRosterWarning(!result));
    } else {
      setShowPersonNotPresentInRosterWarning(false);
    }
  }, [season, fixture, person, role, isPersonInRoster]);

  const onSaveHandler = useCallback(async () => {
    const dataSource = (await form.validateFields()) ?? {};
    if (!dataSource.person) {
      throw new Error("The person should exist. This is required field.");
    }

    const upsertValues: UpsertConductData = {
      conductId: isEdit ? conduct?.conductId : undefined,
      upsertData: {
        conductType: dataSource.conductType,
        personId: dataSource.person.value,
        role: dataSource.role?.value === RolePlayer ? null : dataSource.role?.value || null,
        competitionId: dataSource.competition?.value || "",
        seasonId: dataSource.season?.value || "",
        fixtureId: dataSource.fixture?.value || "",
        dateFinePaidLocal: dataSource.dateFinePaidLocal?.format("YYYY-MM-DD") || null,
        dateOffenceLocal: dataSource.dateOffenceLocal?.format("YYYY-MM-DDTHH:mm:ss.s") || null,
        dateSuspendedFrom: dataSource.dateSuspendedFrom?.format("YYYY-MM-DD") || null,
        dateSuspendedTo: dataSource.dateSuspendedTo?.format("YYYY-MM-DD") || null,
        fineAmount: dataSource.fineAmount ?? 0,
        fineCurrency: dataSource.fineCurrency,
        fineStatus: dataSource.fineStatus,
        status: dataSource.status,
        lifeSentence: dataSource.lifeSentence,
        conductNotes: dataSource.conductNotes,
      },
    };
    onUpsert(upsertValues);
    onClose();
  }, [form, onClose, onUpsert, isEdit, conduct?.conductId]);

  return (
    <Modal
      width={580}
      open={true}
      onCancel={onClose}
      okText={formatMessage({ id: isEdit ? "update" : "create" })}
      onOk={onSaveHandler}
      okButtonProps={{ loading: isUpsertInProcess }}
    >
      <StyledForm form={form} labelCol={{ span: 8 }} wrapperCol={{ span: 16 }} initialValues={formInitValues} style={{ marginTop: "20px" }}>
        <Form.Item
          name="person"
          label={formatMessage({ id: "conduct.upsert.person" })}
          tooltip={formatMessage({ id: "form.help.conduct.person" })}
          validateStatus={showPersonNotPresentInRosterWarning ? "warning" : undefined}
          help={showPersonNotPresentInRosterWarning ? showPersonNotPresentInRosterWarningMessage : undefined}
          rules={[
            {
              required: true,
              message: formatMessage({ id: "validation.message.required" }, { item: formatMessage({ id: "conduct.upsert.person" }) }),
            },
          ]}
          style={formItemSharedStyles}
        >
          <SelectPerson placeholder="" disabled={isUpsertInProcess} onChange={(_) => form.setFieldsValue({ role: null })} />
        </Form.Item>

        <Form.Item<UpsertConductForm>
          name={"role"}
          label={formatMessage({ id: "conduct.upsert.role" })}
          tooltip={formatMessage({ id: "form.help.conduct.role" })}
          rules={[
            {
              required: true,
              message: formatMessage({ id: "validation.message.required" }, { item: formatMessage({ id: "conduct.upsert.role" }) }),
            },
          ]}
          style={formItemSharedStyles}
        >
          <SelectRole
            disabled={!person || isUpsertInProcess}
            placeholder=""
            personId={person?.value}
            seasonId={season?.value}
            fixtureId={fixture?.value}
            additionalOptions={[
              {
                label: formatMessage({ id: "conduct.role.player" }),
                value: RolePlayer,
              },
            ]}
          />
        </Form.Item>

        <Form.Item<UpsertConductForm>
          name={"conductType"}
          rules={[
            {
              required: true,
              message: formatMessage({ id: "validation.message.required" }, { item: formatMessage({ id: "conduct.upsert.conductType" }) }),
            },
          ]}
          label={formatMessage({ id: "conduct.upsert.conductType" })}
          tooltip={formatMessage({ id: "form.help.conduct.conductType" })}
          style={formItemSharedStyles}
        >
          <Select mode="multiple" showSearch={false} disabled={isUpsertInProcess} options={conductTypes} />
        </Form.Item>

        <Form.Item<UpsertConductForm>
          name={"competition"}
          label={formatMessage({ id: "conduct.upsert.competition" })}
          tooltip={formatMessage({ id: "form.help.conduct.competition" })}
          style={formItemSharedStyles}
        >
          <SelectCompetition
            placeholder=""
            disabled={isUpsertInProcess}
            onChange={() => form.setFieldsValue({ season: null, fixture: null })}
          />
        </Form.Item>

        <Form.Item<UpsertConductForm>
          name={"season"}
          label={formatMessage({ id: "conduct.upsert.season" })}
          tooltip={formatMessage({ id: "form.help.conduct.season" })}
          style={formItemSharedStyles}
        >
          <SelectSeason
            disabled={!competition || isUpsertInProcess}
            placeholder=""
            competitionId={competition?.value}
            onChange={(_) => form.setFieldsValue({ fixture: null })}
          />
        </Form.Item>

        <Form.Item<UpsertConductForm>
          name={"fixture"}
          label={formatMessage({ id: "conduct.upsert.fixture" })}
          tooltip={formatMessage({ id: "form.help.conduct.fixture" })}
          style={formItemSharedStyles}
        >
          <SelectFixture disabled={!season || isUpsertInProcess} placeholder="" seasonId={season?.value} />
        </Form.Item>

        <Form.Item<UpsertConductForm>
          name={"conductNotes"}
          label={formatMessage({ id: "conduct.upsert.conductNotes" })}
          tooltip={formatMessage({ id: "form.help.conduct.conductNotes" })}
          style={formItemSharedStyles}
        >
          <Input.TextArea disabled={isUpsertInProcess} />
        </Form.Item>

        <Form.Item<UpsertConductForm>
          name={"dateOffenceLocal"}
          label={formatMessage({ id: "conduct.upsert.dateOffenceLocal" })}
          tooltip={formatMessage({ id: "form.help.conduct.dateOffenceLocal" })}
          style={formItemSharedStyles}
        >
          <DatePicker disabled={isUpsertInProcess} style={{ width: "100%" }} format="DD/MM/YY" />
        </Form.Item>

        <Form.Item<UpsertConductForm>
          name={"dateSuspendedFrom"}
          label={formatMessage({ id: "conduct.upsert.dateSuspendedFrom" })}
          tooltip={formatMessage({ id: "form.help.conduct.dateSuspendedFrom" })}
          style={formItemSharedStyles}
        >
          <DatePicker disabled={isUpsertInProcess} style={{ width: "100%" }} format="DD/MM/YY" />
        </Form.Item>

        <Form.Item<UpsertConductForm>
          name={"dateSuspendedTo"}
          label={formatMessage({ id: "conduct.upsert.dateSuspendedTo" })}
          tooltip={formatMessage({ id: "form.help.conduct.dateSuspendedTo" })}
          style={formItemSharedStyles}
        >
          <DatePicker disabled={isUpsertInProcess} style={{ width: "100%" }} format="DD/MM/YY" />
        </Form.Item>

        <Form.Item<UpsertConductForm>
          name={"lifeSentence"}
          label={formatMessage({ id: "conduct.upsert.lifeSentence" })}
          tooltip={formatMessage({ id: "form.help.conduct.lifeSentence" })}
          style={formItemSharedStyles}
        >
          <Switch disabled={isUpsertInProcess} />
        </Form.Item>

        <Form.Item<UpsertConductForm>
          name={"fineAmount"}
          label={formatMessage({ id: "conduct.upsert.fineAmount" })}
          tooltip={formatMessage({ id: "form.help.conduct.fineAmount" })}
          style={formItemSharedStyles}
        >
          <InputNumber disabled={isUpsertInProcess} precision={2} style={{ width: "100%" }} />
        </Form.Item>

        <Form.Item<UpsertConductForm>
          name={"fineCurrency"}
          label={formatMessage({ id: "conduct.upsert.fineCurrency" })}
          tooltip={formatMessage({ id: "form.help.conduct.fineCurrency" })}
          style={formItemSharedStyles}
        >
          <Input disabled={isUpsertInProcess} />
        </Form.Item>

        <Form.Item<UpsertConductForm>
          name={"fineStatus"}
          label={formatMessage({ id: "conduct.upsert.fineStatus" })}
          tooltip={formatMessage({ id: "form.help.conduct.fineStatus" })}
          style={formItemSharedStyles}
        >
          <Select allowClear disabled={isUpsertInProcess} options={fineStatuses} showSearch={false} />
        </Form.Item>

        <Form.Item<UpsertConductForm>
          name={"dateFinePaidLocal"}
          label={formatMessage({ id: "conduct.upsert.dateFinePaidLocal" })}
          tooltip={formatMessage({ id: "form.help.conduct.dateFinePaidLocal" })}
          style={formItemSharedStyles}
        >
          <DatePicker style={{ width: "100%" }} disabled={isUpsertInProcess} format="DD/MM/YY" />
        </Form.Item>

        <Form.Item<UpsertConductForm>
          name={"status"}
          label={formatMessage({ id: "conduct.upsert.status" })}
          tooltip={formatMessage({ id: "form.help.conduct.status" })}
          style={formItemSharedStyles}
        >
          <Select disabled={isUpsertInProcess} options={statuses} showSearch={false} />
        </Form.Item>
      </StyledForm>
    </Modal>
  );
};

export default ConductUpsertModal;
